import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, Trash2, Archive, RotateCcw, AlertTriangle, Building2 } from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../../contexts/AuthContext";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";

const opts = { withCredentials: true };

export function ComputoPapeleraModal({ open, onClose, onChanged, tipo = "computo", title }) {
  const { user } = useAuth();
  const role = user?.role || "";
  const canPermanent = role === "Superadministrador" || role === "Administrador";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  // confirm: { type: "single"|"empty", id?, name? }
  const [confirm, setConfirm] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/computo-tasks/papelera?tipo=${tipo}`, opts);
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch { toast.error("Error al cargar la papelera"); }
    finally { setLoading(false); }
  }, [tipo]);

  useEffect(() => { if (open) fetchItems(); }, [open, fetchItems]);

  const handleRestore = async (t) => {
    try {
      await axios.post(`${API}/computo-tasks/${t._id}/restore`, {}, opts);
      toast.success("Restaurado");
      fetchItems();
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al restaurar"); }
  };

  const doPermanent = async (id) => {
    setBusy(true);
    try {
      await axios.delete(`${API}/computo-tasks/${id}/permanent`, opts);
      toast.success("Eliminado permanentemente");
      fetchItems();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    } finally { setBusy(false); setConfirm(null); }
  };

  const doEmpty = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/computo-tasks/papelera/empty?tipo=${tipo}`, {}, opts);
      toast.success(`Papelera vaciada (${data.deleted || 0})`);
      fetchItems();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al vaciar la papelera");
    } finally { setBusy(false); setConfirm(null); }
  };

  const daysLeft = (deletedAt) => {
    if (!deletedAt) return 30;
    const passed = Math.floor((Date.now() - new Date(deletedAt).getTime()) / 86400000);
    return Math.max(0, 30 - passed);
  };

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <Archive size={18} /> {title || "Papelera"}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">Retención automática: 30 días desde la eliminación</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100" data-testid="computo-papelera-close-btn">
            <X size={18} />
          </button>
        </div>

        {!loading && items.length > 0 && canPermanent && (
          <div className="px-6 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-end">
            <button
              onClick={() => setConfirm({ type: "empty" })}
              disabled={busy}
              className="flex items-center gap-1.5 border-2 border-red-300 text-red-700 hover:bg-red-50 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors disabled:opacity-60"
              data-testid="computo-papelera-empty-btn"
            >
              <AlertTriangle size={12} /> Vaciar Papelera
            </button>
          </div>
        )}

        <div className="p-6 flex-1 overflow-y-auto" data-testid="computo-papelera-list">
          {loading ? (
            <div className="space-y-2">{[1, 2].map(i => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Archive size={36} className="mx-auto mb-2" />
              <p className="text-sm font-medium">Papelera vacía</p>
            </div>
          ) : (
            <div className="space-y-2">
              {items.map(t => {
                const dl = daysLeft(t.deleted_at);
                return (
                  <div
                    key={t._id}
                    className="border border-gray-200 bg-gray-50 rounded-xl p-4 flex items-center gap-3"
                    data-testid={`computo-papelera-item-${t._id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-gray-900 text-sm break-words">{t.budget_nombre}</p>
                      <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1 break-words">
                        <span className="font-mono text-[#1A4B7A] font-bold">{t.budget_number}</span>
                        <span>·</span>
                        <Building2 size={11} /> {t.cliente || "—"}
                        <span>· Eliminado por {t.deleted_by_name || "—"}</span>
                      </p>
                      <p className="text-xs mt-0.5">
                        <span className={`font-semibold ${dl <= 5 ? "text-red-600" : "text-amber-600"}`}>{dl} día{dl !== 1 ? "s" : ""} restantes</span>
                      </p>
                    </div>
                    <button
                      onClick={() => handleRestore(t)}
                      className="px-3 py-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-semibold flex items-center gap-1 transition-colors"
                      data-testid={`computo-restore-btn-${t._id}`}
                    >
                      <RotateCcw size={12} /> Restaurar
                    </button>
                    {canPermanent && (
                      <button
                        onClick={() => setConfirm({ type: "single", id: t._id, name: t.budget_nombre })}
                        className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        data-testid={`computo-permanent-delete-${t._id}`}
                        aria-label="Eliminar permanentemente"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {confirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="computo-papelera-confirm-modal">
          <div className="absolute inset-0 bg-black/60" onClick={() => !busy && setConfirm(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle size={20} className="text-red-600" />
              </div>
              <div className="flex-1">
                <h4 className="text-base font-bold text-gray-900">
                  {confirm.type === "empty" ? "¿Vaciar la papelera completa?" : "¿Eliminar permanentemente?"}
                </h4>
                <p className="text-sm text-gray-600 mt-1.5">
                  {confirm.type === "empty"
                    ? `Se borrarán de forma definitiva las ${items.length} tarea(s) de la papelera.`
                    : <>La tarea <span className="font-semibold">"{confirm.name}"</span> se borrará para siempre.</>}
                </p>
                <p className="text-xs text-red-600 mt-2 font-bold">Esta acción NO se puede deshacer.</p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirm(null)} disabled={busy}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-60"
                data-testid="computo-papelera-confirm-cancel-btn"
              >
                Cancelar
              </button>
              <button
                onClick={() => confirm.type === "empty" ? doEmpty() : doPermanent(confirm.id)}
                disabled={busy}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-60"
                data-testid="computo-papelera-confirm-delete-btn"
              >
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                {confirm.type === "empty" ? "Sí, vaciar" : "Sí, eliminar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
